/**
 * Footer 的連結資料（依語系切換文案與網址）
 *
 * @returns {
 *   columns: 四個連結欄位,
 *   socialLinks: 社群連結,
 *   legalLinks: 底部必要連結
 * }
 */

export interface IFooterLink {
  name: string;
  url: string;
}

export interface IFooterColumn {
  title: string;
  links: IFooterLink[];
}

export interface IFooterSocialLink extends IFooterLink {
  icon: string;
}

const SOLUTION_PATHS: Record<string, string> = {
  'iems-solutions': '/solutions/iems-solutions',
  'wise-iotsuite': '/solutions/wise-iotsuite',
  ivisionsuite: '/solutions/ivisionsuite',
  ifactory: '/solutions/ifactory',
  'wise-edge-developer-architecture':
    '/topics/wise-edge-developer-architecture',
  'digital-om-energy': '/topics/digital-om-energy',
};

const CN_SOLUTION_PATHS: Record<string, string> = {
  'ifactory-fnb': '/solutions/ifactory/FNB',
  'ifactory-molding': '/solutions/ifactory/molding',
  'ifactory-pcb': '/solutions/ifactory/pcb',
  'ifactory-automotive': '/solutions/ifactory/automotive',
  'iems-solutions': '/solutions/iems-solutions',
  'ifactory-tpm': '/solutions/ifactory/tpm',
};

const CONTAINER_CATALOG_URL =
  'https://catalog.advantech.com/en-int/containers?pageIndex=1';
const LEARNING_HUB_URL = 'https://learn.advantech.com/';
const WEDA_PATH = '/products/advantech.weda';
const FEATURED_PRODUCTS_CODE = 'featured-products';
const FEATURED_PRODUCTS_COUNT = 5;

export function useFooterMenu() {
  const { $i18n } = useNuxtApp();
  const { t } = $i18n;

  const { isCN } = useLanguage();
  const { provideFormatUrl } = useFormatUrl();
  const {
    privacyPolicyUrl,
    technicalUrl,
    trainingUrl,
    advantechUrl,
    securityAdvisoryUrl,
  } = useWebsiteUrl();

  const menuStore = useMenuStore();
  const { formatProductsMenu } = storeToRefs(menuStore);

  const aboutLinks = computed((): IFooterLink[] => [
    { name: t('footer.about.overview'), url: provideFormatUrl('/about-us') },
    // ponytail: /partner/find-a-partner 尚未建置，先指向現有的 Ecosystem 頁
    {
      name: t('footer.about.find-a-partner'),
      url: provideFormatUrl('/partners/Ecosystem'),
    },
    {
      name: t('footer.about.partner-program'),
      url: provideFormatUrl('/partners'),
    },
    {
      name: t('footer.about.membership'),
      url: provideFormatUrl('/membership'),
    },
    {
      name: t('footer.about.success-stories'),
      url: provideFormatUrl('/news?type=caseStudies'),
    },
  ]);

  const productLinks = computed((): IFooterLink[] => {
    const category = formatProductsMenu.value.find(
      ({ code }) => code === FEATURED_PRODUCTS_CODE
    );
    const products = (category?.subMenus ?? []).flatMap(
      ({ subMenus }) => subMenus
    );

    const items = provideFooterProducts(products, {
      wedaPath: WEDA_PATH,
      // CN 站的 WEDA 頁面尚未上線，不置頂，直接依 Dropdown 順序補滿
      isPinWeda: !isCN.value,
      limit: FEATURED_PRODUCTS_COUNT,
    });

    return [
      ...items.map(({ name, url }) => ({
        name,
        url: provideFormatUrl(url),
      })),
      {
        name: t('footer.products.view-all'),
        // ponytail: 產品概覽頁尚未建置，先連到 Featured Products 分類頁
        url: provideFormatUrl(
          category?.link?.url ?? `/category/${FEATURED_PRODUCTS_CODE}`
        ),
      },
    ];
  });

  const solutionLinks = computed((): IFooterLink[] =>
    Object.entries(isCN.value ? CN_SOLUTION_PATHS : SOLUTION_PATHS).map(
      ([code, path]) => ({
        name: t(`footer.solutions.${code}`),
        url: provideFormatUrl(path),
      })
    )
  );

  const resourceLinks = computed((): IFooterLink[] => [
    {
      name: t('footer.resources.container-catalog'),
      url: CONTAINER_CATALOG_URL,
    },
    { name: t('footer.resources.learning-hub'), url: LEARNING_HUB_URL },
    // CN 站沒有技術文件這一項
    ...(isCN.value
      ? []
      : [
          {
            name: t('footer.resources.documentation'),
            url: technicalUrl.value,
          },
        ]),
    { name: t('footer.resources.training'), url: trainingUrl.value },
    {
      name: t('footer.resources.webinars'),
      url: provideFormatUrl('/webinars'),
    },
    { name: t('footer.resources.news'), url: provideFormatUrl('/news') },
  ]);

  const columns = computed((): IFooterColumn[] => [
    { title: t('footer.about.title'), links: aboutLinks.value },
    { title: t('footer.products.title'), links: productLinks.value },
    { title: t('footer.solutions.title'), links: solutionLinks.value },
    { title: t('footer.resources.title'), links: resourceLinks.value },
  ]);

  const socialLinks = computed((): IFooterSocialLink[] =>
    isCN.value
      ? [
          {
            name: 'QQ',
            icon: 'IconQq',
            url: 'https://wpa1.qq.com/p2kDuAV4?_type=wpa&qidian=true',
          },
          {
            name: 'WeChat',
            icon: 'IconWechat',
            url: 'https://work.weixin.qq.com/ca/cawcde03c95bd23203',
          },
          {
            name: 'bilibili',
            icon: 'IconBilibili',
            url: 'https://space.bilibili.com/1361781007/upload/video',
          },
        ]
      : [
          {
            name: 'YouTube',
            icon: 'IconYoutube',
            url: 'https://www.youtube.com/@AdvantechCorp',
          },
          {
            name: 'Facebook',
            icon: 'IconFacebook',
            url: 'https://www.facebook.com/Advantech.Corp',
          },
          {
            name: 'LinkedIn',
            icon: 'IconLinkedin',
            url: 'https://www.linkedin.com/company/advantech/',
          },
        ]
  );

  const legalLinks = computed((): IFooterLink[] => [
    { name: t('footer.about-advantech'), url: advantechUrl.value },
    { name: t('footer.privacy-policy'), url: privacyPolicyUrl.value },
    { name: t('footer.security-advisory'), url: securityAdvisoryUrl.value },
  ]);

  return { columns, socialLinks, legalLinks };
}
